import { deleteCookie, readCookie, writeCookie } from '@/shared/lib/cookies'
import { backendUrl, getRuntimeConfig, webFrontendUrl } from '@/shared/config/runtime'

/** Matches `REFRESH_TOKEN_LIFETIME` on the backend. */
const REFRESH_TOKEN_MAX_AGE_SECONDS = 60 * 60 * 24 * 7

const REFRESH_TOKEN_COOKIE = 'jwt_token'
const USER_SESSION_COOKIE = 'user_session'

/**
 * Cookie names carry a configurable prefix so two instances on sibling
 * subdomains do not overwrite each other's session.
 */
function cookieName(key: string): string {
  return `${getRuntimeConfig().cookiePrefix}${key}`
}

function baseOptions() {
  return {
    maxAge: REFRESH_TOKEN_MAX_AGE_SECONDS,
    sameSite: getRuntimeConfig().sameSiteCookie,
    secure: webFrontendUrl().startsWith('https://'),
  }
}

/**
 * The longest domain the frontend and the backend have in common, or null.
 *
 * `secure_file_serve` authenticates plain `GET` downloads, which carry no
 * `Authorization` header, from the `user_session` cookie. When the backend is on a
 * sibling subdomain -- `api.example.com` against `app.example.com` -- a host-only
 * cookie never reaches it, so the download 403s while everything else works.
 *
 * Deliberately not a public suffix lookup. The result of one would only ever be
 * this same shared suffix, because both hosts sharing a registrable domain means
 * sharing every label of it; and the case the list exists to catch -- a suffix
 * that is public, like `co.uk` for two unrelated sites -- is one the browser
 * already refuses. Letting it refuse is both smaller than bundling the list and
 * more accurate than a snapshot of it that goes stale.
 */
export function sharedCookieDomain(
  frontendHost: string = window.location.hostname,
): string | null {
  const backendHost = new URL(backendUrl()).hostname
  if (backendHost === frontendHost) return null

  const backendLabels = backendHost.split('.').reverse()
  const frontendLabels = frontendHost.split('.').reverse()

  const shared: string[] = []
  for (let i = 0; i < Math.min(backendLabels.length, frontendLabels.length); i += 1) {
    if (backendLabels[i] !== frontendLabels[i]) break
    shared.unshift(backendLabels[i] as string)
  }

  // One label is either a public suffix on its own or a hostname with no domain
  // part, and neither is something a cookie may be set on.
  return shared.length >= 2 ? shared.join('.') : null
}

export function setRefreshTokenCookie(token: string): void {
  writeCookie(cookieName(REFRESH_TOKEN_COOKIE), token, baseOptions())
}

export function readRefreshTokenCookie(): string | null {
  return readCookie(cookieName(REFRESH_TOKEN_COOKIE))
}

/**
 * Stores the signed session, widened to reach the backend where that is possible.
 *
 * The widened write is checked rather than assumed: a browser silently drops a
 * cookie whose `Domain` is a public suffix, and an unchecked write would leave no
 * session cookie at all -- losing it on reload as well as failing to authenticate
 * a download, which is the worse of the two failures.
 */
export function setUserSessionCookie(signedSession: string): void {
  const name = cookieName(USER_SESSION_COOKIE)
  clearUserSessionCookie()

  const domain = sharedCookieDomain()
  if (domain !== null) {
    writeCookie(name, signedSession, { ...baseOptions(), domain })
    if (readCookie(name) !== null) return
  }

  writeCookie(name, signedSession, baseOptions())
}

function clearUserSessionCookie(): void {
  const name = cookieName(USER_SESSION_COOKIE)
  // Both variants: expiring the host-only one leaves a widened cookie in place and
  // still being sent to the backend, which is how a signed-out browser keeps
  // downloading files.
  deleteCookie(name)
  const domain = sharedCookieDomain()
  if (domain !== null) {
    deleteCookie(name, { domain })
  }
}

export function clearSessionCookies(): void {
  deleteCookie(cookieName(REFRESH_TOKEN_COOKIE))
  clearUserSessionCookie()
}
